import type { FrameRecords } from "../types/api";

// Match-block pairing for the Side-by-Side tab. A direct port of the Excel paired
// sheets (reporting/paired_rows.py::_side_block_ids / _ins_block_ids /
// _build_paired_rows) so the browser aligns a CRI row against the SAME partner the
// workbook does — one rule, two renderers, no second definition of "matched with".
//
// Block key: match_group_id when set (N:M aggregate/pool matches share one id), else
// `pair::<cri_index>` for a 1:1 match. matched_row_id on a CRI row is the partner
// INSURER row's index and vice versa, so a 1:1 pair's two rows must key on the SAME
// index: the CRI row keys on its OWN index, the insurer row keys on its
// matched_row_id (the partner CRI index) — both resolve to the CRI index.

export interface PairedRow {
  // Row positions into the filtered CRI / insurer slices; null = blank band on that
  // side (ragged fill for N:M, or a partner that lives elsewhere).
  cri: number | null;
  ins: number | null;
  // The block this row belongs to, so a renderer can band a multi-row group together.
  // Opaque — compare for equality, never parse.
  blockId: string;
  // First row of a multi-row (N:M aggregate) block — drives the group separator.
  // 1:1 pairs get none, matching the Excel writer (avoids a border on every row).
  groupStart: boolean;
  // The row's BLOCK has >=1 row on each side. A single-sided row in such a block is
  // N:M overflow (its partner was consumed by another leg of the same group); a
  // single-sided row whose block is one-sided has its partner elsewhere (cross-track).
  bothSided: boolean;
}

// Value is present (not null/undefined/NaN/blank). matched_row_id arrives as a
// JSON number or null; a NaN float serialises to null, so this covers both.
function has(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === "number") return !Number.isNaN(v);
  const s = String(v).trim();
  return s !== "" && s !== "nan" && s !== "NaN" && s !== "None";
}

// Normalise an index-like cell to a stable string key. matched_row_id is stored
// float64 (NaN-able) server-side, so 11 and 11.0 must produce the SAME key as the
// partner's integer index — `pair::11`, never `pair::11.0`.
function idxKey(v: unknown): string {
  if (typeof v === "number") return String(Math.trunc(v));
  const s = String(v).trim();
  const n = Number(s);
  return Number.isFinite(n) ? String(Math.trunc(n)) : s;
}

// Per-row block ids for one side. `ownIndex` selects the 1:1 fallback key: the CRI
// side keys on its own frame index, the insurer side on its matched_row_id.
function blockIds(
  frame: FrameRecords,
  rows: number[],
  useOwnIndex: boolean,
  orphanPrefix: string
): string[] {
  const cGid = frame.columns.indexOf("match_group_id");
  const cMrid = frame.columns.indexOf("matched_row_id");
  let orphan = 0;
  return rows.map((pos) => {
    const gid = cGid >= 0 ? frame.rows[pos][cGid] : null;
    if (has(gid)) return String(gid);
    if (useOwnIndex) return `pair::${idxKey(frame.indices[pos])}`;
    const partner = cMrid >= 0 ? frame.rows[pos][cMrid] : null;
    if (has(partner)) return `pair::${idxKey(partner)}`;
    // Neither id (should not occur inside a Matched/Mismatch slice) → stands alone.
    return `${orphanPrefix}${orphan++}`;
  });
}

/**
 * Interleave a CRI row slice and an insurer row slice into aligned paired rows,
 * grouped by match block. Within a block the CRI rows fill the left band and the
 * insurer rows the right, aligned top-down; the shorter side leaves trailing blanks
 * (an N:M group genuinely has an unequal count per side). Block order follows first
 * CRI appearance, then any insurer-only blocks — same as the workbook.
 *
 * `criRows` / `insRows` are row POSITIONS into the respective frames (already
 * filtered/bucketed by the caller); the returned positions index those same frames.
 */
export function buildPairedRows(
  criFrame: FrameRecords,
  criRows: number[],
  insFrame: FrameRecords,
  insRows: number[]
): PairedRow[] {
  const criIds = blockIds(criFrame, criRows, true, "orphan::c");
  const insIds = blockIds(insFrame, insRows, false, "orphan::i");

  // Insertion-ordered: JS Map preserves insertion order, matching the OrderedDict.
  const blocks = new Map<string, { cri: number[]; ins: number[] }>();
  const get = (id: string) => {
    let b = blocks.get(id);
    if (!b) { b = { cri: [], ins: [] }; blocks.set(id, b); }
    return b;
  };
  criIds.forEach((id, i) => get(id).cri.push(criRows[i]));
  insIds.forEach((id, i) => get(id).ins.push(insRows[i]));

  const out: PairedRow[] = [];
  for (const [id, blk] of blocks) out.push(...interleaveBlock(blk.cri, blk.ins, id));
  return out;
}

/**
 * Align ONE match block's two sides into paired rows: CRI rows fill the left band and
 * insurer rows the right, top-down, with the shorter side leaving trailing blanks (a
 * many-to-one group genuinely has an unequal count per side).
 *
 * Split out so a caller that already KNOWS its rows are one match — the applied /
 * remembered manual-match panels, the suggested-match queue, the release panel — pairs
 * them by exactly the rule the Side-by-Side tab and the Excel paired sheets use, rather
 * than a second definition of "which row lines up with which". Those callers must not go
 * through buildPairedRows: it derives blocks from match_group_id / matched_row_id, which
 * a manual group has not been given yet (and an unapplied remembered pair never gets), so
 * its legs would scatter into one block each.
 */
export function interleaveBlock(criRows: number[], insRows: number[],
                                blockId = "block"): PairedRow[] {
  const n = Math.max(criRows.length, insRows.length);
  const bothSided = criRows.length > 0 && insRows.length > 0;
  const out: PairedRow[] = [];
  for (let k = 0; k < n; k++) {
    out.push({
      cri: k < criRows.length ? criRows[k] : null,
      ins: k < insRows.length ? insRows[k] : null,
      blockId,
      groupStart: k === 0 && n > 1,
      bothSided,
    });
  }
  return out;
}

// Why a single-sided row has an empty band. Mirrors sheet_specs._single_sided_note:
// inside a both-sided block the partner was consumed by another leg of a many-to-one
// match; a one-sided block means the partner sits on the other track. A partner that
// is genuinely absent gets no note rather than a guess.
export function singleSidedNote(bothSided: boolean, partnerElsewhere: boolean): string {
  if (bothSided) return "↳ part of an aggregated (many-to-one) match — see this group";
  if (partnerElsewhere) return "↳ counterpart is on the other track";
  return "";
}
